import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Play,
  Building2, 
  Users, 
  Loader, 
  AlertTriangle, 
  ArrowRight, 
  Coffee,
  Recycle,
  Bug,
  Leaf
} from 'lucide-react';
import { signInAsDemo } from '../lib/auth';

const DemoPage: React.FC = () => {
  const navigate = useNavigate(); 
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDemoAccess = async () => {
    try {
      setLoading(true);
      setError('');
      
      const result = await signInAsDemo();
      
      if (result.success) {
        navigate('/dashboard'); 
      } else {
        setError(result.error || 'Failed to access demo account');
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center p-4 bg-avn-purple-600 rounded-full mb-6">
              <Play className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              AVN Demo Experience
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Explore our platform through interactive demos showcasing our innovative solutions for sustainable agriculture.
            </p>
          </div>

          {/* Demo Options */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Edward's Hamlet Demo */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-avn-purple-100 p-3 rounded-lg">
                  <Building2 className="h-6 w-6 text-avn-purple-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">Edward's Hamlet</h2>
                  <p className="text-gray-600">Regenerative farming hub in Uganda</p>
                </div>
              </div>
              
              <p className="text-gray-700 mb-6">
                Experience Edward's farm management dashboard with real-time data, analytics, 
                and insights powered by AVN-Earth technology. See how our platform helps 
                smallholder farmers increase yields and access premium markets.
              </p>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2">
                  <Coffee className="h-5 w-5 text-avn-purple-600" />
                  <span>Unity Bean coffee production</span>
                </div>
                <div className="flex items-center gap-2">
                  <Recycle className="h-5 w-5 text-avn-purple-600" />
                  <span>Circular value-added products</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-avn-purple-600" />
                  <span>273 farmer cooperative</span>
                </div>
              </div>
              
              {error && (
                <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                  <p>{error}</p>
                </div>
              )}
              
              <div className="flex flex-col gap-4">
                <button
                  onClick={handleDemoAccess}
                  disabled={loading}
                  className="w-full bg-avn-purple-600 text-white py-3 px-6 rounded-lg hover:bg-avn-purple-700 transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader className="h-5 w-5 animate-spin" />
                      <span>Accessing Demo...</span>
                    </>
                  ) : (
                    <>
                      <span>Access Dashboard Demo</span>
                      <ArrowRight className="h-5 w-5" />
                    </>
                  )}
                </button>
                
                <a
                  href="/edwards-hamlet"
                  className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                >
                  <span>Learn About Edward's Hamlet</span>
                  <ArrowRight className="h-5 w-5" />
                </a>
              </div>
            </div>
            
            {/* BSF Demo */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-green-100 p-3 rounded-lg">
                  <Bug className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">🪲 Durban BSF Feed</h2>
                  <p className="text-gray-600">Circular economy waste management</p>
                </div>
              </div>
              
              <p className="text-gray-700 mb-6">
                Explore how Black Soldier Fly (BSF) larvae are transforming organic waste into 
                high-protein animal feed and organic fertilizer, creating a circular economy 
                solution for sustainable agriculture in Durban, South Africa.
              </p>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2">
                  <Bug className="h-5 w-5 text-green-600" />
                  <span>Waste-to-protein conversion</span>
                </div>
                <div className="flex items-center gap-2">
                  <Leaf className="h-5 w-5 text-green-600" />
                  <span>Organic fertilizer production</span>
                </div>
                <div className="flex items-center gap-2">
                  <Recycle className="h-5 w-5 text-green-600" />
                  <span>Zero-waste circular system</span>
                </div>
              </div>
              
              <button
                onClick={() => navigate('/bsf-demo')}
                className="w-full bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
              >
                <span>Explore BSF Dashboard</span>
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          <div className="text-center text-gray-600">
            <p>
              These demos provide a realistic preview of our platform's capabilities. 
              For a personalized experience tailored to your specific needs, 
              <a href="/signup" className="text-avn-purple-600 hover:text-avn-purple-700"> sign up for free</a>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoPage;